import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { t } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert/Alert';
import { Button } from '@/components/ui/Button/Button';

/**
 * Forbidden Page
 * 
 * Displayed when a user tries to access a page they don't have permission for.
 * Follows UI/UX guidelines for error states with clear messaging and helpful actions.
 */
export function Forbidden() {
  usePageHeader({ title: t('Access Denied'), breadcrumbs: [] });

  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract the attempted path from state if available
  const attemptedPath = (location.state as any)?.from || 'the requested page';
  const message = (location.state as any)?.message || 'You don\'t have permission to access this resource';

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  return (
    <PageContainer>
      <div className="max-w-2xl mx-auto mt-8">
        <Alert variant="error" className="mb-6">
          <div className="flex items-start gap-3">
            <svg 
              className="w-5 h-5 flex-shrink-0 mt-0.5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
            <div>
              <p className="font-medium">Permission Required</p>
              <p className="text-sm mt-1 opacity-90">{message}</p>
            </div>
          </div>
        </Alert>

        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              {/* Illustration */}
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-error-100 dark:bg-error-900/20 flex items-center justify-center">
                <svg 
                  className="w-12 h-12 text-error-600 dark:text-error-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                  />
                </svg>
              </div>

              {/* Heading */}
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Access Restricted
              </h2>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                This page requires permissions you don't currently have. Please contact your organization administrator to request access.
              </p>

              {/* Actions */}
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleGoBack}
                >
                  <svg 
                    className="w-4 h-4 mr-2" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M10 19l-7-7m0 0l7-7m-7 7h18" 
                    />
                  </svg>
                  Go Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleGoHome}
                >
                  <svg 
                    className="w-4 h-4 mr-2" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                    />
                  </svg>
                  Go to Dashboard
                </Button>
              </div>

              {/* Help text */}
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
                Need help? Contact your organization administrator or visit our{' '}
                <a href="/help" className="text-primary-600 dark:text-primary-400 hover:underline">
                  help center
                </a>
                .
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}

